import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Grow from "@mui/material/Grow";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import "../styles_web/Animations.css";
import css from "../styles_web/HeaderProfileMenu.module.css";
import { useNavigate } from "react-router-dom";

/************************************/
/* Header Profile Menu */
/************************************/
const HeaderProfileMenu = ({
  onClosed,
  userName,
  profileTitle,
  storeLogo,
  storeName,
  profileMenu,
  authMenu,
  onSignInOut,
}) => {

  const navigate = useNavigate();

  const handleItemClick = (item, e) => {
    //fix to ensure menu closes: Prevent link navigation,
    //close menu, and then queue navigation.
    e.preventDefault();
    onClosed();
    setTimeout(() => navigate(item.link));
  };

  return (
    <Box className={css["menu-box"]}>
      <span>{userName}</span>
      <br />
      <span className={css["menu-profiletitle"]}>{profileTitle}</span>

      {/* Profile Menus */}
      {/* Titled, multiple sections */}
      <div className={css["menu-divider"]}></div>
      {profileMenu &&
        profileMenu.length > 1 &&
        profileMenu.map((menu, i) => (
          <div key={i}>
            <HeaderProfileMenuSection
              title={menu.title}
              link={menu.link}
              menuItems={menu?.menuItems}
              onItemClick={handleItemClick}
            ></HeaderProfileMenuSection>
            {i !== profileMenu.length - 1 && (
              <div className={css["menu-divider"]}></div>
            )}
          </div>
        ))}
      {/* Titleless, single section */}
      {profileMenu &&
        profileMenu.length <= 1 &&
        profileMenu.map((menu, i) => (
          <div key={i}>
            <HeaderProfileMenuSection
              menuItems={menu?.menuItems}
              onItemClick={handleItemClick}
            ></HeaderProfileMenuSection>
          </div>
        ))}

      {/* Auth Menu */}
      {/* Only show additional divider if there are profile menus above */}
      {profileMenu && profileMenu.length > 0 && (
        <div className={css["menu-divider"]}></div>
      )}
      {authMenu &&
        authMenu.map((item, i) => (
          <div key={i}>
            <HeaderProfileMenuSection
              title={item.title}
              menuItems={item?.menuItems}
              onItemClick={onSignInOut}
            ></HeaderProfileMenuSection>
            {i !== authMenu.length - 1 && (
              <div className={css["menu-divider"]}></div>
            )}
          </div>
        ))}
    </Box>
  );
};

HeaderProfileMenu.propTypes = {
  onClosed: PropTypes.func,
  userName: PropTypes.string.isRequired,
  profileTitle: PropTypes.string,
  storeName: PropTypes.string,
  storeLogo: PropTypes.string,
  profileMenu: PropTypes.array,
  authMenu: PropTypes.array,
  onSignInOut: PropTypes.func,
};

/************************************/
/* Header Profile Menu Section */
/************************************/
const HeaderProfileMenuSection = ({ title, link, menuItems, onItemClick }) => {
  return (
    <div>
      {title && !link && (
        <span className={css["menu-section-title"]}>{title}</span>
      )}
      {title && link && (
        <span className={css["menu-section-title"]}>
          <Link onClick={(e) => onItemClick({ title, link }, e)} to={link}>
            {title}
          </Link>
        </span>
      )}
      {menuItems && (
        <ul>
          {menuItems.map((item) => (
            <li key={item.title}>
              <Link
                onClick={(e) =>
                  onItemClick({ title: item.title, link: item.link }, e)
                }
                to={item.link}
              >
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

HeaderProfileMenuSection.propTypes = {
  menuItems: PropTypes.array,
  title: PropTypes.string,
  link: PropTypes.string,
  onItemClick: PropTypes.func,
};

/************************************/
/* Header Profile Popper Menu */
/************************************/
const HeaderProfilePopperMenu = ({
  isOpen,
  onClosed,
  onSignInOut,
  anchorEl,
  userName,
  profileTitle,
  storeName,
  storeLogo,
  profileMenu,
  authMenu,
}) => {
  const popperRef = useRef();

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        popperRef.current &&
        !popperRef.current.contains(e.target) &&
        anchorEl &&
        !anchorEl.contains(e.target)
      ) {
        onClosed();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    const resized = (e) => {
      const width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      //sized to mobile
      if (width < 1200) onClosed();
    };
    window.addEventListener("resize", resized);
    window.addEventListener("scroll", onClosed);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", resized);
      window.removeEventListener("scroll", onClosed);
    };
  }, [anchorEl, popperRef, onClosed]);

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-end"
      ref={popperRef}
      className={css["menu"]}
    >
      <Grow in={true}>
        <div>
          <div className={css["menu-point"]}></div>
          <HeaderProfileMenu
            onClosed={onClosed}
            userName={userName}
            profileTitle={profileTitle}
            storeName={storeName}
            storeLogo={storeLogo}
            profileMenu={profileMenu}
            authMenu={authMenu}
            onSignInOut={onSignInOut}
          ></HeaderProfileMenu>
        </div>
      </Grow>
    </Popper>
  );
};

HeaderProfilePopperMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClosed: PropTypes.func.isRequired,
  onSignInOut: PropTypes.func.isRequired,
  anchorEl: PropTypes.object.isRequired,
  userName: PropTypes.string.isRequired,
  profileTitle: PropTypes.string,
  storeName: PropTypes.string,
  storeLogo: PropTypes.string,
  profileMenu: PropTypes.array,
  authMenu: PropTypes.array,
};

export default HeaderProfilePopperMenu;
